import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { ThoughtBubbleFlow, ThoughtBubbleStep } from '../types/thought-bubble';
import type FeaturesService from './features';
import type ThoughtBubbleFlowModel from '../models/thought-bubble-flow';
import type ThoughtBubbleStepModel from '../models/thought-bubble-step';

// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

const STORAGE_KEY = 'tio-thought-bubble-flows';

type FlowRecordAttributes = Pick<
  ThoughtBubbleFlowModel,
  | 'name'
  | 'description'
  | 'flowType'
  | 'app'
  | 'route'
  | 'targetingRules'
  | 'enabledDomains'
  | 'status'
  | 'priority'
>;

type StepRecordAttributes = Pick<
  ThoughtBubbleStepModel,
  | 'elementSelector'
  | 'title'
  | 'description'
  | 'side'
  | 'align'
  | 'disableActiveInteraction'
  | 'order'
  | 'flow'
>;

// ─── Model ↔ Plain Object Conversion ─────────────────────────────────────────

function modelToStep(record: ThoughtBubbleStepModel): ThoughtBubbleStep {
  return {
    id: record.id ?? undefined,
    elementSelector: record.elementSelector ?? '',
    title: record.title ?? '',
    description: record.description ?? '',
    side: (record.side as ThoughtBubbleStep['side']) ?? 'bottom',
    align: (record.align as ThoughtBubbleStep['align']) ?? 'center',
    disableActiveInteraction: record.disableActiveInteraction ?? false,
    order: record.order ?? 0,
  };
}

function modelToFlow(record: ThoughtBubbleFlowModel): ThoughtBubbleFlow {
  const steps = (record.steps?.slice() ?? []).map(modelToStep).sort((a, b) => a.order - b.order);
  // Prefer the persisted flowType when available. If missing (API doesn't persist it),
  // infer from steps: if all steps lack an elementSelector, it's a centered flow.
  // Only infer 'no-element' when selectors are truly null/undefined, not just empty strings,
  // to avoid misclassifying partially configured element flows.
  const persistedType = record.flowType as ThoughtBubbleFlow['type'] | undefined;
  const type: ThoughtBubbleFlow['type'] =
    persistedType && (persistedType === 'element' || persistedType === 'no-element')
      ? persistedType
      : steps.length > 0 && steps.every((s) => !s.elementSelector?.trim())
        ? 'no-element'
        : 'element';

  return {
    id: record.id ?? '',
    type,
    app: (record.app as ThoughtBubbleFlow['app']) ?? 'admin',
    name: record.name ?? '',
    description: record.description ?? '',
    route: record.route ?? '',
    steps,
    targetingRules: record.targetingRules ?? { rules: [], matchMode: '', firstLoginOnly: false },
    enabledDomains: record.enabledDomains ?? [],
    status: (record.status as ThoughtBubbleFlow['status']) ?? 'draft',
    priority: record.priority ?? 0,
    invocationsCount: record.invocationsCount,
    companyInvocationsCount: record.companyInvocationsCount,
    createdAt: record.createdAt ?? new Date().toISOString(),
    updatedAt: record.updatedAt ?? new Date().toISOString(),
  };
}

// ─── Service ──────────────────────────────────────────────────────────────────

export default class ThoughtBubbleStorageService extends Service {
  @service declare store: StoreService;
  @service declare features: FeaturesService;

  @tracked revision = 0;
  private loaded = false;
  private loadPromise: Promise<void> | null = null;
  private stagedFlows = new Map<string, ThoughtBubbleFlow>();

  /**
   * Use the API backend on all environments except local development.
   */
  get useApi() {
    return this.features.tioEnvironment !== 'local';
  }

  // ─── Public API ─────────────────────────────────────────────────────

  /**
   * Ensure flows are loaded from the API. No-op for localStorage mode.
   * Safe to call multiple times — deduplicates concurrent calls.
   */
  async ensureLoaded(): Promise<void> {
    if (!this.useApi || this.loaded) return;
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = this.fetchAllFlows();
    try {
      await this.loadPromise;
    } finally {
      this.loadPromise = null;
    }
  }

  get allFlows(): ThoughtBubbleFlow[] {
    if (!this.useApi) {
      void this.revision;
      return this.getLocalFlows();
    }
    const storeFlows = this.store
      .peekAll('thought-bubble-flow')
      .map((r) => modelToFlow(r as ThoughtBubbleFlowModel));
    // Include any staged (not yet persisted) flows
    void this.revision;
    return [...storeFlows, ...this.stagedFlows.values()];
  }

  getAllFlows() {
    return this.allFlows;
  }

  getFlow(id: string) {
    if (!this.useApi) {
      return this.getLocalFlows().find((f) => f.id === id);
    }
    // Check staged flows first (not yet saved to API)
    const staged = this.stagedFlows.get(id);
    if (staged) return staged;
    const record = this.store.peekRecord(
      'thought-bubble-flow',
      id
    ) as ThoughtBubbleFlowModel | null;
    return record ? modelToFlow(record) : undefined;
  }

  getActiveFlows() {
    return this.allFlows.filter((f) => f.status === 'active');
  }

  async stageFlow(flow: ThoughtBubbleFlow): Promise<void> {
    if (!this.useApi) {
      this.saveLocalFlow(flow);
    } else {
      await this.ensureLoaded();
      this.stagedFlows.set(flow.id, flow);
      this.revision++;
    }
  }

  /**
   * Cache a flow locally without making API calls.
   * Used for cross-window sync where the source window manages persistence.
   */
  cacheFlowLocally(flow: ThoughtBubbleFlow): void {
    if (!this.useApi) {
      this.saveLocalFlow(flow);
    } else {
      this.stagedFlows.set(flow.id, flow);
      this.revision++;
    }
  }

  async saveFlow(flow: ThoughtBubbleFlow): Promise<void> {
    if (!this.useApi) {
      this.saveLocalFlow(flow);
      return;
    }
    if (this.stagedFlows.has(flow.id)) {
      this.stagedFlows.delete(flow.id);
      await this.createFlowViaStore(flow);
    } else {
      const record = this.store.peekRecord(
        'thought-bubble-flow',
        flow.id
      ) as ThoughtBubbleFlowModel | null;
      if (record) {
        await this.updateFlowViaStore(flow, record);
      } else {
        await this.createFlowViaStore(flow);
      }
    }
  }

  async deleteFlow(id: string): Promise<void> {
    if (!this.useApi) {
      this.deleteLocalFlow(id);
      return;
    }
    const record = this.peekFlowRecord(id);
    if (record) {
      await record.destroyRecord();
    }
  }

  async updateFlowStatus(id: string, status: ThoughtBubbleFlow['status']): Promise<void> {
    if (!this.useApi) {
      this.updateLocalFlowStatus(id, status);
      return;
    }
    const record = this.store.peekRecord(
      'thought-bubble-flow',
      id
    ) as ThoughtBubbleFlowModel | null;
    if (!record) return;
    record.status = status;
    await record.save();
  }

  async updateFlowPriority(id: string, priority: number): Promise<void> {
    if (!this.useApi) {
      this.updateLocalFlowPriority(id, priority);
      return;
    }
    const record = this.store.peekRecord(
      'thought-bubble-flow',
      id
    ) as ThoughtBubbleFlowModel | null;
    if (!record) return;
    record.priority = priority;
    await record.save();
  }

  async duplicateFlow(id: string): Promise<ThoughtBubbleFlow | undefined> {
    if (!this.useApi) {
      return this.duplicateLocalFlow(id);
    }
    const record = this.store.peekRecord(
      'thought-bubble-flow',
      id
    ) as ThoughtBubbleFlowModel | null;
    if (!record) return undefined;
    const original = modelToFlow(record);
    const copy: ThoughtBubbleFlow = {
      ...original,
      id: '',
      name: `${original.name} (Copy)`,
      status: 'draft',
      steps: original.steps.map((s) => ({ ...s, id: undefined })),
    };
    return this.createFlowViaStore(copy);
  }

  exportFlows() {
    return JSON.stringify(this.allFlows, null, 2);
  }

  async importFlows(json: string): Promise<{ count: number }> {
    if (!this.useApi) {
      return this.importLocalFlows(json);
    }
    return this.importFlowsViaStore(json);
  }

  // ─── Store Operations ──────────────────────────────────────────────

  private async fetchAllFlows() {
    try {
      await this.store.query('thought-bubble-flow', { include: 'steps' });
    } catch (e) {
      console.error('Failed to fetch thought bubble flows', e);
    } finally {
      this.loaded = true;
    }
  }

  private async createFlowViaStore(flow: ThoughtBubbleFlow) {
    const record = this.createFlowRecord({
      name: flow.name,
      description: flow.description,
      flowType: flow.type,
      app: flow.app,
      route: flow.route,
      targetingRules: flow.targetingRules,
      enabledDomains: flow.enabledDomains,
      status: flow.status,
      priority: flow.priority ?? 0,
    });
    await record.save();

    for (const step of flow.steps) {
      const stepRecord = this.createStepRecord({
        elementSelector: step.elementSelector ?? '',
        title: step.title,
        description: step.description,
        side: step.side,
        align: step.align,
        disableActiveInteraction: step.disableActiveInteraction,
        order: step.order,
        flow: record,
      });
      await stepRecord.save();
    }

    return modelToFlow(record as ThoughtBubbleFlowModel);
  }

  private async updateFlowViaStore(flow: ThoughtBubbleFlow, record: ThoughtBubbleFlowModel) {
    record.name = flow.name;
    record.description = flow.description;
    record.flowType = flow.type;
    record.app = flow.app;
    record.route = flow.route;
    record.targetingRules = flow.targetingRules;
    record.enabledDomains = flow.enabledDomains;
    record.status = flow.status;
    record.priority = flow.priority ?? 0;
    await record.save();
    await this.syncSteps(flow, record);
  }

  private async syncSteps(flow: ThoughtBubbleFlow, record: ThoughtBubbleFlowModel) {
    const existingSteps = record.steps?.slice() ?? [];
    const existingIds = new Set(existingSteps.map((s) => s.id));
    const currentIds = new Set(flow.steps.filter((s) => s.id).map((s) => s.id as string));

    // Delete removed steps
    for (const step of existingSteps) {
      if (step.id && !currentIds.has(step.id)) {
        await step.destroyRecord();
      }
    }

    // Create or update each step
    for (const step of flow.steps) {
      if (step.id && existingIds.has(step.id)) {
        const stepRecord = this.peekStepRecord(step.id);
        if (stepRecord) {
          stepRecord.elementSelector = step.elementSelector ?? '';
          stepRecord.title = step.title;
          stepRecord.description = step.description;
          stepRecord.side = step.side;
          stepRecord.align = step.align;
          stepRecord.disableActiveInteraction = step.disableActiveInteraction;
          stepRecord.order = step.order;
          await stepRecord.save();
        }
      } else {
        const stepRecord = this.createStepRecord({
          elementSelector: step.elementSelector ?? '',
          title: step.title,
          description: step.description,
          side: step.side,
          align: step.align,
          disableActiveInteraction: step.disableActiveInteraction,
          order: step.order,
          flow: record,
        });
        await stepRecord.save();
      }
    }
  }

  private peekFlowRecord(id: string) {
    return this.store.peekRecord('thought-bubble-flow', id) as ThoughtBubbleFlowModel | null;
  }

  private peekStepRecord(id: string) {
    return this.store.peekRecord('thought-bubble-step', id) as ThoughtBubbleStepModel | null;
  }

  private createFlowRecord(attributes: FlowRecordAttributes) {
    return this.store.createRecord('thought-bubble-flow', attributes) as ThoughtBubbleFlowModel;
  }

  private createStepRecord(attributes: StepRecordAttributes) {
    return this.store.createRecord('thought-bubble-step', attributes) as ThoughtBubbleStepModel;
  }

  private async importFlowsViaStore(json: string) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      throw new Error('Invalid JSON: unable to parse flows');
    }
    if (!Array.isArray(parsed)) {
      throw new Error('Invalid format: expected an array of flows');
    }

    const flows = parsed as ThoughtBubbleFlow[];
    for (const flow of flows) {
      const toCreate = {
        ...flow,
        id: '',
        steps: flow.steps.map((s) => ({ ...s, id: undefined })),
      };
      await this.createFlowViaStore(toCreate);
    }
    return { count: flows.length };
  }

  // ─── localStorage Methods ──────────────────────────────────────────

  private getLocalFlows(): ThoughtBubbleFlow[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ThoughtBubbleFlow[]) : [];
    } catch {
      return [];
    }
  }

  private saveLocalFlows(flows: ThoughtBubbleFlow[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flows));
    this.revision++;
  }

  private saveLocalFlow(flow: ThoughtBubbleFlow) {
    const flows = this.getLocalFlows();
    const index = flows.findIndex((f) => f.id === flow.id);
    const now = new Date().toISOString();
    const updated = { ...flow, updatedAt: now };

    if (index >= 0) {
      flows[index] = updated;
    } else {
      flows.push({ ...updated, createdAt: now });
    }

    this.saveLocalFlows(flows);
  }

  private deleteLocalFlow(id: string) {
    const flows = this.getLocalFlows().filter((f) => f.id !== id);
    this.saveLocalFlows(flows);
  }

  private updateLocalFlowStatus(id: string, status: ThoughtBubbleFlow['status']) {
    const flows = this.getLocalFlows();
    const flow = flows.find((f) => f.id === id);
    if (flow) {
      flow.status = status;
      flow.updatedAt = new Date().toISOString();
      this.saveLocalFlows(flows);
    }
  }

  private updateLocalFlowPriority(id: string, priority: number) {
    const flows = this.getLocalFlows();
    const flow = flows.find((f) => f.id === id);
    if (flow) {
      flow.priority = priority;
      flow.updatedAt = new Date().toISOString();
      this.saveLocalFlows(flows);
    }
  }

  private duplicateLocalFlow(id: string) {
    const original = this.getLocalFlows().find((f) => f.id === id);
    if (!original) return undefined;

    const copy: ThoughtBubbleFlow = {
      ...structuredClone(original),
      id: crypto.randomUUID(),
      name: `${original.name} (Copy)`,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const flows = this.getLocalFlows();
    flows.push(copy);
    this.saveLocalFlows(flows);
    return copy;
  }

  private importLocalFlows(json: string) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      throw new Error('Invalid JSON: unable to parse flows');
    }

    if (!Array.isArray(parsed)) {
      throw new Error('Invalid format: expected an array of flows');
    }

    const imported = parsed as Record<string, unknown>[];
    const now = new Date().toISOString();

    const normalized: ThoughtBubbleFlow[] = imported.map((item) => {
      const flow = { ...item };
      if (typeof flow['id'] !== 'string' || !flow['id']) {
        flow['id'] = crypto.randomUUID();
      }
      if (typeof flow['status'] !== 'string' || !flow['status']) {
        flow['status'] = 'draft';
      }
      if (typeof flow['createdAt'] !== 'string') {
        flow['createdAt'] = now;
      }
      if (typeof flow['updatedAt'] !== 'string') {
        flow['updatedAt'] = now;
      }
      if (
        !flow['targetingRules'] ||
        typeof flow['targetingRules'] !== 'object' ||
        !Array.isArray((flow['targetingRules'] as Record<string, unknown>)['rules'])
      ) {
        flow['targetingRules'] = { rules: [], matchMode: '', firstLoginOnly: false };
      }
      if (!Array.isArray(flow['enabledDomains'])) {
        flow['enabledDomains'] = [];
      }
      return flow as unknown as ThoughtBubbleFlow;
    });

    const existing = this.getLocalFlows();
    const existingIds = new Set(existing.map((f) => f.id));

    for (const flow of normalized) {
      if (existingIds.has(flow.id)) {
        const idx = existing.findIndex((f) => f.id === flow.id);
        if (idx >= 0) existing[idx] = flow;
      } else {
        existing.push(flow);
      }
    }

    this.saveLocalFlows(existing);
    return { count: normalized.length };
  }
}

declare module '@ember/service' {
  interface Registry {
    'thought-bubble-storage': ThoughtBubbleStorageService;
  }
}
